import { definePersistenceEntry, type ModuleListener, type UiModule } from '@krp/client-runtime'

import { AQSI_ACTIONS } from './actions/flowActions'
import { createCatalogListeners } from './features/catalog/model/listeners'
import { deliveryLiveHandlers } from './features/delivery/api/liveHandlers'
import { createDeliveryListeners } from './features/delivery/model/listeners'
import { createIncidentsListeners } from './features/incidents/model/listeners'
import { createReceiptsListeners } from './features/receipts/model/listeners'
import { createRefundListeners } from './features/refunds/model/listeners'
import { decodeRefundCommands, refundCommandsRestored } from './features/refunds/model/refundCommandsSlice'
import { decodeReceiptsViews, viewsRestored } from './features/receipts/model/receiptsSlice'
import { rmkLiveHandlers } from './features/rmk/api/liveHandlers'
import { createRmkListeners } from './features/rmk/model/listeners'
import { cartDraftsRestored, decodeCartDrafts } from './features/sale/model/cartSlice'
import { createSaleListeners } from './features/sale/model/listeners'
import { decodePaymentAttempts, paymentAttemptsRestored } from './features/sale/model/paymentAttemptsSlice'
import { decodeSaleCommands, saleCommandsRestored } from './features/sale/model/saleCommandsSlice'
import { decodeOperations, operationsRestored } from './features/rmk/model/operationsSlice'
import { decodeShiftReads, shiftReadsRestored } from './features/rmk/model/shiftReadsSlice'
import { decodeStartMemories, startMemoryRestored } from './features/rmk/model/startSlice'
import { aqsiShell } from './shell'
import { aqsiUiReducer, type AqsiUiState } from './state'
import { aqsiWindows } from './windows/registerWindows'

/** Сколько живёт запомненная команда смены: дольше смены кассы ждать её незачем. */
const OPERATIONS_TTL_MS = 12 * 60 * 60 * 1000

/** Подтверждение кода кассира служба выдаёт не дольше чем на 10 минут; запись живёт чуть дольше — просроченное отбросит сам декодер. */

/** Курсор журнала чеков живёт на сервере ограниченное время (по умолчанию 30 минут): запись старше не нужна. */
const RECEIPTS_VIEW_TTL_MS = 25 * 60 * 1000

/** Несохранённая правка корзины живёт недолго: дольше пары часов смены она устарела, дальше правду говорит продажа на сервере. */
const CART_QUEUE_TTL_MS = 2 * 60 * 60 * 1000

/** Сколько живёт запомненная команда оплаты: дольше смены ждать её исход незачем — дальше правду говорит продажа. */
const SALE_COMMANDS_TTL_MS = 12 * 60 * 60 * 1000

/** Попытка оплаты живёт, как команда оплаты: дальше правду говорит продажа. */
const PAYMENT_ATTEMPTS_TTL_MS = 12 * 60 * 60 * 1000

/** Запомненная команда возврата живёт, как команда оплаты: дальше правду говорит касса. */
const REFUND_COMMANDS_TTL_MS = 12 * 60 * 60 * 1000

/**
 * Модуль пакета для composition. Endpoints пакет регистрирует сам
 * (`baseApi.injectEndpoints` в своих `features/<фича>/api`, имена `aqsi*`, rid в
 * аргументах и тегах); приложение подключает модуль одной строкой.
 */
export function createAqsiUiModule(): UiModule<AqsiUiState> {
  const listeners: ModuleListener<AqsiUiState>[] = [
    ...createRmkListeners<AqsiUiState>((state) => state.operations, (state) => state.cashierAccess),
    ...createCatalogListeners<AqsiUiState>(),
    ...createReceiptsListeners<AqsiUiState>(),
    ...createDeliveryListeners<AqsiUiState>(),
    ...createSaleListeners<AqsiUiState>((state) => ({ sale: state.sale, cart: state.cart, commands: state.saleCommands, attempts: state.paymentAttempts, fast: state.fastReceipts })),
    ...createRefundListeners<AqsiUiState>((state) => ({ refunds: state.refunds, commands: state.refundCommands })),
    ...createIncidentsListeners<AqsiUiState>(),
  ]
  return {
    id: 'aqsi',
    reducer: aqsiUiReducer,
    slices: [
      { name: 'shiftReads', lifetime: 'session', reason: 'время запроса смены: F5 не ускоряет повторное чтение; факт смены не хранится' },
      {
        name: 'operations',
        lifetime: 'session',
        reason:
          'operationId отправленной команды смены: F5 и обрыв сети продолжают проверять ту же операцию, а не шлют новую',
      },
      {
        name: 'start',
        lifetime: 'device',
        reason:
          'выбор в «Начале работы» (кассир, вид цены, касса) и «как в прошлый раз» на этом устройстве; код кассира не хранится никогда (RP-FB22)',
      },
      {
        name: 'cashierAccess',
        lifetime: 'session',
        reason:
          'подтверждение кода кассира (≤ 10 минут) и блокировка после неверных кодов: F5 возвращает их без нового ввода кода; сообщение проверки живёт только в памяти вкладки',
      },
      {
        name: 'opening',
        lifetime: 'screen',
        reason: 'ход «Открыть смену» в этой вкладке; после F5 правду говорят работа за кассой и статус команды на сервере',
      },
      { name: 'feedback', lifetime: 'screen', reason: 'итог последнего нажатия; после F5 правду говорит сервер' },
      {
        name: 'catalog',
        lifetime: 'screen',
        reason: 'страница каталога и отметки проверки профиля — дело одного захода',
      },
      {
        name: 'receipts',
        lifetime: 'session',
        reason: 'отбор и позиция в журнале чеков переживают F5; курсор просрочится на сервере — тогда «начать заново»',
      },
      {
        name: 'shifts',
        lifetime: 'screen',
        reason: 'отбор раздела «Смены» (период, место, кассир, состояние, «только мои»); вкладка помнит его своим снимком, смены — в кэше',
      },
      { name: 'delivery', lifetime: 'screen', reason: 'срез операций доставки и ход сверки — не работа, а взгляд' },
      {
        name: 'sale',
        lifetime: 'screen',
        reason:
          'несохранённая правка корзины (секунды до debounce), поиск подбора, черновик ручной сверки; сама продажа — account, на сервере',
      },
      {
        name: 'cart',
        lifetime: 'session',
        reason:
          'очередь корзины: несохранённая правка и замороженный запрос отправки (ключ идемпотентности, версия, тело): F5 и обрыв сети не теряют правку, очередь продолжает её тем же ключом (RP-SALE-FAST)',
      },
      {
        name: 'saleCommands',
        lifetime: 'session',
        reason:
          'operationId отправленной оплаты/чека/отмены: F5 и обрыв сети не разблокируют «Оплатить», пока чтение продажи не покажет исход',
      },
      {
        name: 'paymentAttempts',
        lifetime: 'session',
        reason:
          'попытка оплаты и нажатия кассира («Отменить», «Закрыть», «Свернуть», ручная сверка): F5 не открывает окно прошлого отказа и не разворачивает свёрнутое; ход команд — только в памяти вкладки (RP-PAY-ONE)',
      },
      {
        name: 'fastReceipts',
        lifetime: 'screen',
        reason:
          'память вкладки о продажах, чек которых печатается в фоне (быстрая оплата): чтобы показать «Чек пробит» и не открыть окно итога над новой корзиной; ход чека читается с сервера, после F5 панель собирается из списка отложенных продаж (RP-SALE-FAST, шаг 9)',
      },
      {
        name: 'refunds',
        lifetime: 'screen',
        reason: 'отмеченные к возврату количества и итог возврата в окне; сам возврат — account, на сервере',
      },
      {
        name: 'refundCommands',
        lifetime: 'session',
        reason:
          'operationId отправленного возврата/денег/чека/отмены: F5 и обрыв сети не разблокируют «Вернуть», пока чтение кассы не покажет исход',
      },
      {
        name: 'incidents',
        lifetime: 'screen',
        reason: 'позиция в журнале инцидентов (курсор живёт на сервере 30 минут) и предел журнала расхождений',
      },
      { name: 'prices', lifetime: 'screen', reason: 'поиск и страница прайса кабинета — дело одного захода; цены перечитываются' },
    ],
    windows: aqsiWindows,
    // UI-ACTIONS: подсказки ⓘ кнопок потока «Смены» → «Новая смена» → продажа (UI-RMK-FLOW 3.13).
    actions: AQSI_ACTIONS,
    // LIVE: события касс и доставки службы AQSI — что перечитать без перезагрузки.
    live: [...rmkLiveHandlers, ...deliveryLiveHandlers],
    persistence: [
      definePersistenceEntry({
        key: 'aqsi.shift-reads', slice: 'shiftReads', lifetime: 'session', scope: 'user', version: 1,
        ttlMs: 60_000,
        pick: (state: AqsiUiState) => state.shiftReads,
        decode: decodeShiftReads, restore: shiftReadsRestored,
      }),
      definePersistenceEntry({
        key: 'aqsi.shift-operations',
        slice: 'operations',
        lifetime: 'session',
        scope: 'user',
        version: 1,
        ttlMs: OPERATIONS_TTL_MS,
        pick: (state: AqsiUiState) => state.operations,
        decode: decodeOperations,
        restore: operationsRestored,
      }),
      definePersistenceEntry({
        key: 'aqsi.rmk-start',
        slice: 'start',
        lifetime: 'device',
        scope: 'user',
        version: 1,
        pick: (state: AqsiUiState) => state.start.byResource,
        decode: decodeStartMemories,
        restore: startMemoryRestored,
      }),
      definePersistenceEntry({
        key: 'aqsi.receipts-view',
        slice: 'receipts',
        lifetime: 'session',
        scope: 'user',
        version: 1,
        ttlMs: RECEIPTS_VIEW_TTL_MS,
        pick: (state: AqsiUiState) => state.receipts.byResource,
        decode: decodeReceiptsViews,
        restore: viewsRestored,
      }),
      definePersistenceEntry({
        key: 'aqsi.cart-queue',
        slice: 'cart',
        lifetime: 'session',
        scope: 'user',
        version: 1,
        ttlMs: CART_QUEUE_TTL_MS,
        pick: (state: AqsiUiState) => state.cart.bySeat,
        decode: decodeCartDrafts,
        restore: cartDraftsRestored,
      }),
      definePersistenceEntry({
        key: 'aqsi.sale-commands',
        slice: 'saleCommands',
        lifetime: 'session',
        scope: 'user',
        // v2 (RP-UI-RMK-FLOW): запись несёт `deviceId` кассы кабинета; записи v1 относились к рабочим местам, которых нет.
        version: 2,
        ttlMs: SALE_COMMANDS_TTL_MS,
        pick: (state: AqsiUiState) => state.saleCommands,
        decode: decodeSaleCommands,
        restore: saleCommandsRestored,
      }),
      definePersistenceEntry({
        key: 'aqsi.payment-attempts',
        slice: 'paymentAttempts',
        lifetime: 'session',
        scope: 'user',
        version: 1,
        ttlMs: PAYMENT_ATTEMPTS_TTL_MS,
        pick: (state: AqsiUiState) => state.paymentAttempts.bySeat,
        decode: decodePaymentAttempts,
        restore: paymentAttemptsRestored,
      }),
      definePersistenceEntry({
        key: 'aqsi.refund-commands',
        slice: 'refundCommands',
        lifetime: 'session',
        scope: 'user',
        // v2 (RP-UI-RMK-FLOW): запись несёт `deviceId` кассы кабинета; записи v1 относились к рабочим местам, которых нет.
        version: 2,
        ttlMs: REFUND_COMMANDS_TTL_MS,
        pick: (state: AqsiUiState) => state.refundCommands,
        decode: decodeRefundCommands,
        restore: refundCommandsRestored,
      }),
    ],
    listeners,
    // SHELL: уведомления и присутствие AQSI собирает оболочка из реестра модулей (RP-SEP5).
    shell: aqsiShell,
  }
}
